import { CloseOutlined, KeyboardArrowUp, Send } from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Fab,
  Fade,
  Grid,
  IconButton,
  Slide,
  Stack,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import Chatbot from "react-chatbot-kit";
import { render } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import AnimateButton from "../../../components/@extended/AnimateButton";
import PopUp from "../../../components/PopUp/PopUp";
import ProductCategoryNav from "../../../components/ProductCategoryNav/ProductCategoryNav";
import ProductSubCategoryNav from "../../../components/ProductCategoryNav/ProductSubCategoryNav";
import {
  RENTAL_ITEM_ATTACHMENT_TARGET,
  RENTAL_ITEM_TARGET,
} from "../../../config/constants";
import ActionProvider from "../../../config/react-chatbot-kit/ActionProvider";
import config from "../../../config/react-chatbot-kit/config";
import MessageParser from "../../../config/react-chatbot-kit/MessageParser";
import socket, { USER_MESSAGE_AVAILABLE } from "../../../socket";
import { userIsAuthenticated } from "../../../stateManagement/helpers";
import { IProductCategory } from "../../../stateManagement/initial-states/service-category.initial-state";
import {
  IAuthState,
  IUser,
} from "../../../stateManagement/reducers/authReducer";
import { stopImitation } from "../../../stateManagement/reducers/imitationsReducer";
import {
  IMessagesState,
  tryFetchMessages,
} from "../../../stateManagement/reducers/messagesReducer";
import { IProductCategoryState } from "../../../stateManagement/reducers/productCategoryReducer";
import { IRentalItemsAttachmentsState } from "../../../stateManagement/reducers/rentalItemsAttachmentsReducer";
import { IRentalItemsState } from "../../../stateManagement/reducers/rentalItemsReducer";
import { IRootReducerT } from "../../../stateManagement/reduxStore";
import Nav from "./Nav";

interface Props {
  children: React.ReactElement;
}

export function ScrollTop(props: Props) {
  const { children } = props;

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector("#back-to-top-anchor");

    if (anchor) {
      anchor.scrollIntoView({
        block: "center",
      });
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 800 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

export function FadeInFadeOut(props: Props) {
  const { children } = props;

  useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    ((event.target as HTMLDivElement).ownerDocument || document).querySelector(
      "#back-to-top-anchor",
    );
  };

  const [show, setShow] = useState(false);

  useEffect(() => {
    setTimeout(() => setShow(true), 5000);
  }, []);

  return (
    <Fade in={show}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: "fixed", bottom: "4rem", right: 16, zIndex: 800 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

interface IProps {
  setShowCartModal: (truth: boolean) => void;
}

const Header = ({ setShowCartModal }: IProps) => {
  const appBarHeight = "0";

  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { user, authToken, realRole } = useSelector<IRootReducerT, IAuthState>(
    (state) => state.auth,
  );
  const { fetchingMessages } = useSelector<IRootReducerT, IMessagesState>(
    (state) => state.messages,
  );
  const { mainRentalItem } = useSelector<IRootReducerT, IRentalItemsState>(
    (state) => state.rentalItems,
  );
  const { mainRentalItemAttachment } = useSelector<
    IRootReducerT,
    IRentalItemsAttachmentsState
  >((state) => state.rentalItemsAttachments);

  const theme = useTheme();
  const idGreaterThanMd = useMediaQuery(theme.breakpoints.up("md"));

  const botImrSrc = "/images/chatBot.png";

  const isAtHome = () => {
    const homePath = "/welcome";
    if (location.pathname === homePath) {
      return true;
    }

    return false;
  };

  const productPagePath = "/products";
  const isAtProductPage = location.pathname === productPagePath;

  const [chatBoxOpened, setChatBoxOpened] = useState(isAtHome() ? true : false);
  const [hideChatBox, setHideChatBox] = useState(isAtHome() ? false : true);
  const [botBtnLogoSrc, setBotBtnLogoSrc] = useState(
    isAtHome() ? "" : botImrSrc,
  );

  const toggleChatBotState = () => {
    if (chatBoxOpened) {
      setChatBoxOpened(false);
      setBotBtnLogoSrc(botImrSrc);
    } else {
      setHideChatBox(false);
    }
  };

  useEffect(() => {
    if (!chatBoxOpened) {
      setTimeout(() => setHideChatBox(true), 250);
    }
  }, [chatBoxOpened]);

  useEffect(() => {
    if (!hideChatBox) {
      setChatBoxOpened(true);
      setBotBtnLogoSrc("/nil");
      setTimeout(() => {
        setBtn(document.querySelector(".react-chatbot-kit-chat-btn-send"));
      }, 250);
    }
  }, [hideChatBox]);

  const trigger = useScrollTrigger();
  const isAwayFromTop = useScrollTrigger({
    disableHysteresis: true,
    threshold: 25,
  });

  const logoHeight = "3rem";
  const LogoImg = styled("img")(() => ({
    height: "100%",
    margin: "auto",
  }));

  /**----------/Set the client's response to socket.io events here/-----------*/

  socket.on(USER_MESSAGE_AVAILABLE, () => {
    if (authToken) {
      if (mainRentalItem) {
        dispatch(
          tryFetchMessages({
            authToken: authToken ?? "",
            targetType: RENTAL_ITEM_TARGET,
            targetId: mainRentalItem._id,
          }),
        );
      }

      if (mainRentalItemAttachment) {
        dispatch(
          tryFetchMessages({
            authToken: authToken ?? "",
            targetType: RENTAL_ITEM_ATTACHMENT_TARGET,
            targetId: mainRentalItemAttachment._id,
          }),
        );
      }
    }
  });

  /**----------------------------------------------------------------------- */

  useEffect(() => {
    const input = document.querySelector(".react-chatbot-kit-chat-input");
    if (input) {
      input.setAttribute("placeholder", "Write a message...");
    }
  }, []);

  const [btn, setBtn] = useState<any>(null);

  useEffect(() => {
    if (user && userIsAuthenticated(user as IUser)) {
      setBtn(document.querySelector(".react-chatbot-kit-chat-btn-send"));
    }
  }, [user]);

  useEffect(() => {
    if (btn) {
      btn.innerHTML = "";
      render(
        <AnimateButton>
          <Send
            color={"black" as "primary"}
            sx={{ height: "2rem !important", width: "2rem !important" }}
          />
        </AnimateButton>,
        btn,
      );

      btn.addEventListener("click", (e: any) => {
        // Select the input element using the class selector
        const input = document.querySelector<HTMLInputElement>(
          ".react-chatbot-kit-chat-input",
        );

        // Check if the input element exists and if its value is empty
        if (input && input.value.trim() === "") {
          e.preventDefault(); // Corrected the method name
          return; // Exit the function early
        }

        setTimeout(() => {
          // Select all images with the class 'userChatbotAvatar'
          const images = document.querySelectorAll(".userChatbotAvatar");

          if (images && (user as IUser)?.imgSrc) {
            // Loop through the NodeList and set the src attribute
            images.forEach((image: any) => {
              image.src = `${process.env.REACT_APP_SERVER_ENDPOINT}/${
                (user as IUser).imgSrc
              }`;
            });
          }
        }, 0);
      });
    }
  }, [btn]);

  const { mainCategory } = useSelector<IRootReducerT, IProductCategoryState>(
    (state) => state.productCategory,
  );

  const [isSubCategoryNavOpen, setIsSubCategoryNavOpen] = useState(false);
  const [previewedCategory, setPreviewedCategory] =
    useState<IProductCategory>(mainCategory);

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          sx={{
            backgroundColor: "transparent !important",
            boxShadow: isAwayFromTop
              ? "0px 6px 18px -8px rgb(58 53 65 / 56%) !important"
              : "none !important",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "0",
              top: "0",
              width: "100%",
              height: "100%",
              backgroundColor: "#faf9f6b8 !important",
              transition: "opacity .25s ease-in-out",
              opacity: isAwayFromTop ? 1 : 0,
              backdropFilter: "blur(2.5px)",
            }}
          ></Box>
          <Container sx={{ p: "0 !important", maxWidth: "100% !important" }}>
            <Toolbar disableGutters>
              <Stack
                sx={{ width: "100%" }}
                onMouseLeave={() => {
                  setIsSubCategoryNavOpen(false);
                }}
              >
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: { xs: "1rem", md: "2rem" },
                    py: { xs: "1rem", md: ".3rem" },
                  }}
                >
                  <Grid item xs="auto">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      spacing=".5rem"
                    >
                      <Box
                        sx={{
                          ml: idGreaterThanMd ? undefined : "1rem",
                          flexGrow: idGreaterThanMd ? undefined : 1,
                          height: idGreaterThanMd ? logoHeight : "2.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <LogoImg
                          onClick={() => {
                            window.open("/", "_self");
                          }}
                          style={{ marginLeft: 0 }}
                          src="/images/logo.png"
                        />
                      </Box>
                      <Stack
                        sx={{ cursor: "pointer" }}
                        alignItems="flex-start"
                        onClick={() => {
                          window.open("/", "_self");
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing=".5rem"
                          sx={{
                            "&>*": {
                              fontSize: "1.7rem !important",
                              lineHeight: "1.7rem !important",
                              letterSpacing: ".025em",
                            },
                          }}
                        >
                          <Typography sx={{ color: "black !important" }}>
                            Rebirth
                          </Typography>
                          <Typography
                            sx={{ color: theme.palette.primary.main }}
                          >
                            Wellness
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing=".5rem"
                          sx={{
                            "&>*": {
                              fontSize: ".6rem !important",
                              letterSpacing: ".28em !important",
                              fontFamily: "Inter !important",
                              color: theme.palette.secondary.main,
                            },
                          }}
                        >
                          <Typography>RENEW</Typography>
                          <Typography>·</Typography>
                          <Typography>RESTORE</Typography>
                          <Typography>·</Typography>
                          <Typography>REBIRTH</Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs="auto" md>
                    <Nav setShowCartModal={setShowCartModal} />
                  </Grid>
                </Grid>
                {isAtProductPage && (
                  <>
                    <ProductCategoryNav
                      setIsSubCategoryNavOpen={setIsSubCategoryNavOpen}
                      setPreviewedCategory={setPreviewedCategory}
                      previewedCategory={previewedCategory}
                      isSubCategoryNavOpen={isSubCategoryNavOpen}
                    />
                    {isSubCategoryNavOpen && (
                      <ProductSubCategoryNav
                        previewedCategory={previewedCategory}
                        setIsOpen={setIsSubCategoryNavOpen}
                      />
                    )}
                  </>
                )}
              </Stack>
            </Toolbar>
          </Container>
          {user &&
            userIsAuthenticated(user as IUser) &&
            realRole &&
            user?.role !== realRole &&
            idGreaterThanMd && (
              <Stack
                sx={{
                  right: ".25rem",
                  top: ".25rem",
                  position: "absolute",
                  width: "fit-content",
                }}
              >
                <AnimateButton>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: "error.main",
                      border: "solid error.main 1px",
                      my: 0,
                      py: ".5rem !important",
                      minHeight: "0 !important",
                      color: "white",
                      "&:hover": {
                        borderColor: "white !important",
                        border: "solid white 1px",
                      },
                    }}
                    onClick={() => {
                      dispatch(stopImitation({ user: user as IUser }));
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "white !important",
                        fontSize: ".7rem !important",
                        lineHeight: ".7rem !important",
                        fontWeight: "700",
                      }}
                    >
                      Stop Customer Mimicry
                    </Typography>
                  </Button>
                </AnimateButton>
              </Stack>
            )}
        </AppBar>
      </Slide>
      <Box sx={{ height: appBarHeight, marginTop: "0 !important" }}></Box>
      <FadeInFadeOut>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: "fit-content",
          }}
        >
          <Stack
            alignItems="flex-end"
            spacing={1}
            sx={{
              width: "fit-content !important",
            }}
          >
            {!hideChatBox && (
              <Fade in={chatBoxOpened}>
                <Stack
                  sx={{
                    width: "fit-content",
                    height: "fit-content",
                    borderRadius: "2.5625rem !important",
                    boxShadow: theme.shadows[10],
                  }}
                >
                  <Chatbot
                    config={config}
                    messageParser={MessageParser}
                    actionProvider={ActionProvider}
                  />
                </Stack>
              </Fade>
            )}
            <AnimateButton>
              <IconButton
                sx={{
                  p: "0 !important",
                  boxShadow: theme.shadows[5],
                }}
                onClick={() => {
                  toggleChatBotState();
                }}
              >
                <Avatar
                  src={botBtnLogoSrc}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    p: ".5rem",
                    "&:hover": { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  <CloseOutlined sx={{ color: "white !important" }} />
                </Avatar>
              </IconButton>
            </AnimateButton>
          </Stack>
          <ScrollTop>
            <Fab
              sx={{
                backgroundColor: theme.palette.primary.main,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
              size="small"
              aria-label="scroll back to top"
            >
              <KeyboardArrowUp sx={{ color: "white !important" }} />
            </Fab>
          </ScrollTop>
        </Stack>
      </FadeInFadeOut>
      {fetchingMessages && (
        <PopUp
          id="fetch-messages"
          notCloseable
          deployed={fetchingMessages}
          setDeployed={(truth: boolean) => true}
        >
          <Box>
            <CircularProgress color="primary" />
          </Box>
        </PopUp>
      )}
    </>
  );
};

export default Header;
